import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import toast from 'react-hot-toast';
import { Search, X } from 'lucide-react';

interface SensitiveWord {
  id: number;
  word: string;
  category: string;
  created_at: string;
  updated_at: string;
}

const AdminSensitiveWords: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [words, setWords] = useState<SensitiveWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [word, setWord] = useState('');
  const [category, setCategory] = useState('general');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [wordToDelete, setWordToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isAuthenticated || (user?.role !== '管理员' && user?.role !== '站长')) {
    return <Navigate to="/" replace />;
  }

  const fetchWords = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        setLoading(false);
        return;
      }
      const response = await fetch('/api/sensitive-words?pageSize=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWords(data.data);
        } else {
          toast.error(data.message || '获取敏感词列表失败');
        }
      } else {
        toast.error('获取敏感词列表失败');
      }
    } catch (error) {
      toast.error('网络错误');
    } finally {
      setLoading(false);
    }
  };

  const addWord = async () => {
    if (!word.trim()) {
      toast.error('敏感词不能为空');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        return;
      }
      const response = await fetch('/api/sensitive-words', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ word, category }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('添加敏感词成功');
          setWord('');
          setCategory('general');
          setShowAddModal(false);
          fetchWords();
        } else {
          toast.error(data.message || '添加敏感词失败');
        }
      } else {
        toast.error('添加敏感词失败');
      }
    } catch (error) {
      toast.error('网络错误');
    }
  };

  const [importWords, setImportWords] = useState('');
  const [importCategory, setImportCategory] = useState('general');

  const handleImport = async () => {
    if (!importWords.trim()) {
      toast.error('请输入敏感词列表');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        return;
      }

      const wordList = importWords
        .split('\n')
        .map(w => w.trim())
        .filter(w => w);

      if (wordList.length === 0) {
        toast.error('请输入有效的敏感词列表');
        return;
      }

      const response = await fetch('/api/sensitive-words/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ words: wordList, category: importCategory }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(`成功导入 ${data.data?.count || 0} 个敏感词`);
          setImportWords('');
          setImportCategory('general');
          setShowImportModal(false);
          fetchWords();
        } else {
          toast.error(data.message || '导入敏感词失败');
        }
      } else {
        toast.error('导入敏感词失败');
      }
    } catch (error) {
      toast.error('网络错误');
    }
  };

  const handleDeleteClick = (id: number) => {
    setWordToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (wordToDelete === null) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('请先登录');
        return;
      }
      const response = await fetch(`/api/sensitive-words/${wordToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('删除敏感词成功');
          fetchWords();
        } else {
          toast.error(data.message || '删除敏感词失败');
        }
      } else {
        toast.error('删除敏感词失败');
      }
    } catch (error) {
      toast.error('网络错误');
    } finally {
      setShowDeleteModal(false);
      setWordToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setWordToDelete(null);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const filteredWords = useMemo(() => {
    if (!searchQuery.trim()) return words;
    const query = searchQuery.toLowerCase();
    return words.filter(w =>
      w.word.toLowerCase().includes(query) ||
      w.category.toLowerCase().includes(query)
    );
  }, [words, searchQuery]);

  useEffect(() => {
    fetchWords();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">敏感词管理</h1>
        <div className="space-x-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition-colors"
          >
            添加敏感词
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
          >
            批量导入
          </button>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索敏感词或分类..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="text-sm text-gray-500 flex items-center">
              找到 {filteredWords.length} 个结果
            </div>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            <p className="mt-2 text-gray-600">加载中...</p>
          </div>
        ) : filteredWords.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-600">{searchQuery ? '未找到匹配结果' : '暂无敏感词'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    敏感词
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    分类
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    创建时间
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWords.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {w.id}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {w.word}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {w.category === 'general' ? '通用' : w.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(w.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleDeleteClick(w.id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">添加敏感词</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  敏感词
                </label>
                <input
                  type="text"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="请输入敏感词"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  分类
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="general">通用</option>
                  <option value="spam">垃圾广告</option>
                  <option value="violence">暴力</option>
                  <option value="politics">政治</option>
                  <option value="porn">色情</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={addWord}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors"
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">批量导入敏感词</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  敏感词列表（每行一个）
                </label>
                <textarea
                  value={importWords}
                  onChange={(e) => setImportWords(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="请输入敏感词列表，每行一个"
                  rows={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  分类
                </label>
                <select
                  value={importCategory}
                  onChange={(e) => setImportCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="general">通用</option>
                  <option value="spam">垃圾广告</option>
                  <option value="violence">暴力</option>
                  <option value="politics">政治</option>
                  <option value="porn">色情</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleImport}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                >
                  导入
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">确认删除</h2>
            <p className="mb-4">确定要删除这个敏感词吗？此操作不可撤销。</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSensitiveWords;
